import { graphyteClickThrough, trackEvent } from '@patrianna/analytics'
import { openDialog } from '@patrianna/dialog-manager'
import type {
  GameProduct,
  GameProductShort,
  SwimlaneCategory,
} from '@patrianna/shared-patrianna-types/store/GameModule'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import Page from 'components/Page'
import { useFilteredGamesByCurrency } from 'hooks/useFilteredGamesByCurrency'
import { useGraphyteSearch } from 'hooks/useGraphyteSearch'
import NotAvailableLocation from 'pageContainers/Home/components/NotAvailableLocation'
import { shouldShowNotAvailableLocation } from 'pageContainers/Home/components/NotAvailableLocation/utils/shouldShowNotAvailableLocation'
import { useAppDispatch } from 'src/store/hooks'
import { startExternalGame } from 'src/store/modules/slotGameFlow/actions'

import SearchInput from '../../components/SearchInput'

import Games from './components/Games'
import classes from './styles.module.scss'

type Props = {
  topGames: GameProduct[]
  categories: SwimlaneCategory[]
  layout: string
}

function SearchPage(props: Props) {
  const { topGames, categories, layout } = props
  const [isFocused, setIsFocused] = useState(false)
  const { search, setSearch, result, searchId, userId, isSearching } = useGraphyteSearch(isFocused)
  const { games, currency } = useFilteredGamesByCurrency({ games: result })

  const dispatch = useAppDispatch()
  const router = useRouter()
  const isResult = Boolean(search && result?.length)
  const inSearch = search.length > 1
  const searchLimit = isResult ? 20 : 10
  const showNotAvailableLocation = shouldShowNotAvailableLocation(categories)
  const refererPath = isResult ? 'actualSearch' : 'defaultSearchResult'

  const handleClick = (game: GameProductShort, gameIndex?: number) => {
    if (game.type === 'bingo') {
      dispatch(startExternalGame(game, router.push, { refererPath, gameIndex, layout }))
    } else {
      openDialog({
        dialogName: 'START_GAME_FLOW',
        dialogProps: {
          game,
          gameIndex,
          refererPath,
          layout,
        },
      })
    }
    graphyteClickThrough(search, searchId, game.code, userId)
  }

  const handleInputFocus = () => {
    trackEvent('search_page_search_input_click')
    setIsFocused(true)
  }

  return showNotAvailableLocation ? (
    <div className={classes.not_available_location_container}>
      <NotAvailableLocation />
    </div>
  ) : (
    <Page className={classes.root}>
      <SearchInput value={search} setValue={setSearch} onInputFocus={handleInputFocus} label='Search games' />
      <Games
        key={currency}
        games={games}
        topGames={topGames}
        startExternalGame={handleClick}
        inSearch={inSearch}
        search={search}
        isResult={isResult}
        limit={searchLimit}
        isSearching={isSearching}
        searchId={searchId}
      />
    </Page>
  )
}

export default SearchPage
